import React, { useState, useEffect, useRef } from 'react';
import { VscChevronDown } from 'react-icons/vsc';
import './SearchSelect.css';

function SearchSelect({ options = [], value, onChange, placeholder = '— None —', disabled, clearable = true }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi]       = useState(0);
  const triggerRef = useRef(null);
  const inputRef   = useRef(null);
  const optsRef    = useRef(null);
  const [dropStyle, setDropStyle] = useState({});

  const selected  = options.find(o => o.value === value);
  const filtered  = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : options;
  const totalOpts = filtered.length + (clearable ? 1 : 0);

  const openDrop = (initialHi) => {
    if (disabled) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropStyle({ position: 'fixed', top: rect.bottom + 2, left: rect.left, minWidth: Math.max(rect.width, 200), zIndex: 9999 });
    setOpen(true);
    setHi(initialHi ?? 0);
  };

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else { setQuery(''); setHi(0); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Scrolling the page closes the dropdown (it is position: fixed) — scrolling its own option list doesn't
    const close = (e) => { if (!optsRef.current?.contains(e.target)) setOpen(false); };
    window.addEventListener('scroll', close, true);
    const clickClose = (e) => {
      if (!triggerRef.current?.closest('.ss')?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', clickClose);
    return () => { window.removeEventListener('scroll', close, true); document.removeEventListener('mousedown', clickClose); };
  }, [open]);

  // Keep the highlighted option visible while arrowing through a long list
  useEffect(() => {
    const list = optsRef.current;
    const opt  = list?.querySelector('.ss-hi');
    if (!opt) return;
    if (opt.offsetTop < list.scrollTop) list.scrollTop = opt.offsetTop;
    else if (opt.offsetTop + opt.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = opt.offsetTop + opt.offsetHeight - list.clientHeight;
  }, [hi, open]);

  const select = (val) => {
    onChange(val);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKey = (e) => {
    if (open) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      return;
    }
    if (e.key === ' ' || (e.altKey && e.key === 'ArrowDown')) {
      e.preventDefault(); e.stopPropagation(); openDrop();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault(); e.stopPropagation();
      if (navigator.clipboard?.readText) {
        navigator.clipboard.readText().then(text => {
          setQuery(text || '');
          openDrop(text ? (clearable ? 1 : 0) : 0);
        }).catch(() => openDrop());
      } else openDrop();
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault(); e.stopPropagation();
      setQuery(e.key);
      openDrop(clearable ? 1 : 0);
      return;
    }
    // Up/Down/Tab/Left/Right bubble to the row navigator
  };

  const handleDropKey = (e) => {
    // Left / Right: close dropdown and let event bubble to the row's keyDown handler
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      setOpen(false);
      return; // do NOT stopPropagation → TR rowNav will handle navigation
    }
    // All other keys stay inside the dropdown
    e.stopPropagation();
    if (e.key === 'Tab')    { setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, totalOpts - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (clearable && hi === 0) select('');
      else { const idx = clearable ? hi - 1 : hi; if (filtered[idx]) select(filtered[idx].value); }
    }
  };

  return (
    <div className={`ss${open ? ' ss-open' : ''}${disabled ? ' ss-dis' : ''}`}>
      <button ref={triggerRef} type="button" className="ss-trigger"
        onClick={openDrop} onKeyDown={handleTriggerKey}>
        <span className={selected ? 'ss-val' : 'ss-ph'}>{selected?.label ?? placeholder}</span>
        <VscChevronDown size={11} className="ss-arr" />
      </button>
      {open && (
        <div className="ss-drop" style={dropStyle}>
          <div className="ss-search-wrap">
            <input ref={inputRef} value={query}
              onChange={e => { const v = e.target.value; setQuery(v); setHi(v && clearable ? 1 : 0); }}
              onKeyDown={handleDropKey}
              placeholder="Search…"
              onClick={e => e.stopPropagation()} />
          </div>
          <div className="ss-opts" ref={optsRef}>
            {clearable && (
              <div className={`ss-opt ss-none${hi === 0 ? ' ss-hi' : ''}`}
                onMouseMove={() => setHi(0)} onClick={() => select('')}>— None —</div>
            )}
            {filtered.map((o, i) => {
              const idx = clearable ? i + 1 : i;
              return (
                <div key={o.value}
                  className={`ss-opt${o.value === value ? ' ss-sel' : ''}${hi === idx ? ' ss-hi' : ''}`}
                  onMouseMove={() => hi !== idx && setHi(idx)} onClick={() => select(o.value)}>
                  {o.label}
                </div>
              );
            })}
            {filtered.length === 0 && <div className="ss-empty">No results</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
